// Payout helpers: paste-to-fill bank details, CSV bulk upload, and the bulk queue views.
import { useCallback, useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { ClipboardPaste, Download, FileUp } from "lucide-react";
import api from "../../../lib/api";
import { Banner, Card, Empty, Modal, Pager, StatusBadge, inr, usePolling, when } from "../../../components/sc/ui";
import { errMsg, TxnDrawer } from "./common";

const IFSC_ANY = /\b([A-Za-z]{4}0[A-Za-z0-9]{6})\b/;
const LABEL = /^(?:a\/?c(?:count)?\s*(?:holder)?\s*(?:name|no\.?|number)?|acc(?:ount)?\s*(?:holder)?\s*(?:name|no\.?|number)?|beneficiary(?:\s*name)?|holder(?:\s*name)?|name|ifsc(?:\s*code)?|bank(?:\s*name)?|branch)\s*[:\-=]\s*/i;

/**
 * Pulls name / account number / IFSC out of copied text, e.g.
 *   SWAROOP  NAIKAR \n 110221414845 \n CNRB0010326 \n MANKI
 * Lines after these (branch / location) are ignored. Labeled text ("A/c no: …") works too.
 * Returns only what it is sure of; anything else stays empty for the user to type.
 */
export function parseBankDetails(text) {
  const out = { name: "", account: "", ifsc: "" };
  if (!text) return out;
  let lines = text.split(/\r?\n|\t|\|/).map((l) => l.trim()).filter(Boolean);
  if (lines.length === 1) lines = lines[0].split(/\s{0,}[,;]\s{0,}|\s+(?=\d{6,})|(?<=\d)\s+(?=[A-Za-z])/).map((l) => l.trim()).filter(Boolean);
  const m = text.match(IFSC_ANY);
  if (m) out.ifsc = m[1].toUpperCase();
  for (const raw of lines) {
    const labelled = LABEL.test(raw);
    const isAcctLabel = /^(a\/?c|acc|account)/i.test(raw) && !/name/i.test(raw.split(/[:\-=]/)[0]);
    const v = raw.replace(LABEL, "").trim();
    const compact = v.replace(/[\s-]/g, "");
    if (out.ifsc && compact.toUpperCase() === out.ifsc) continue;
    if (!out.account && /^\d{6,20}$/.test(compact) && (isAcctLabel || !labelled || /^\d+$/.test(compact))) {
      out.account = compact;
      continue;
    }
    if (!out.name && /^[A-Za-z][A-Za-z .'/]{1,63}$/.test(v) && !/^(ifsc|bank|branch)/i.test(raw)) {
      out.name = v.replace(/\s+/g, " ").trim();
    }
  }
  return out;
}

/** Bank-details paste box for the single payout form. */
export function PasteDetails({ onFill }) {
  const [text, setText] = useState("");
  const last = useRef("");
  const apply = (t, quiet = false) => {
    if (t === last.current) return;
    const d = parseBankDetails(t);
    if (quiet && !(d.account && d.ifsc)) return;
    last.current = t;
    const got = [d.name && "name", d.account && "account number", d.ifsc && "IFSC"].filter(Boolean);
    if (!got.length) return toast.error("Couldn't find account details in that text");
    onFill(d);
    toast.success(`Filled ${got.join(", ")} — check before sending`);
  };
  useEffect(() => {
    const t = setTimeout(() => text.trim() && apply(text, true), 250);
    return () => clearTimeout(t);
  }, [text]); // eslint-disable-line react-hooks/exhaustive-deps
  const fromClipboard = async () => {
    try {
      const t = await navigator.clipboard.readText();
      setText(t);
      apply(t);
    } catch {
      toast.error("Clipboard blocked — paste into the box instead");
    }
  };
  return (
    <div className="po-paste">
      <div className="ui-between" style={{ gap: 8 }}>
        <span className="ui-eyebrow">Auto-fill from copied details</span>
        <button type="button" className="ui-btn ui-btn-sm" data-testid="po-paste-clip" onClick={fromClipboard}>
          <ClipboardPaste size={14} /> Paste
        </button>
      </div>
      <textarea className="ui-input is-mono" data-testid="po-paste" rows={3} value={text}
        placeholder={"Name\nAccount number\nIFSC"}
        onChange={(e) => setText(e.target.value)} />
    </div>
  );
}

// ─────────────────────────── CSV ───────────────────────────

/** RFC 4180 CSV → array of rows (handles quotes, commas and newlines inside quotes). */
export function parseCsv(text) {
  const rows = [];
  let row = [], cell = "", q = false;
  const s = text.replace(/^﻿/, "");
  for (let i = 0; i < s.length; i++) {
    const c = s[i];
    if (q) {
      if (c === '"') { if (s[i + 1] === '"') { cell += '"'; i++; } else q = false; }
      else cell += c;
    } else if (c === '"') q = true;
    else if (c === ",") { row.push(cell); cell = ""; }
    else if (c === "\n" || c === "\r") {
      if (c === "\r" && s[i + 1] === "\n") i++;
      row.push(cell); cell = "";
      if (row.some((x) => x.trim() !== "")) rows.push(row);
      row = [];
    } else cell += c;
  }
  row.push(cell);
  if (row.some((x) => x.trim() !== "")) rows.push(row);
  return rows;
}

export const SAMPLE_CSV = "reference_id,beneficiary_name,account_number,ifsc_code,bank_name,mobile_number,email,amount,transfer_mode\n"
  + "30317,RAHUL SHARMA,34066655664,SBIN0006166,JANEPHAL,9049212200,rahul@example.com,3400,IMPS\n";

function downloadSample() {
  const a = document.createElement("a");
  a.href = URL.createObjectURL(new Blob([SAMPLE_CSV], { type: "text/csv" }));
  a.download = "supercash_bulk_payout_sample.csv";
  a.click();
  URL.revokeObjectURL(a.href);
}

const ITEM_TONE = { queued: "pending", sending: "sending", sent: "success", failed: "failed", skipped: "dropped", cancelled: "dropped" };

export function BulkUpload({ onClose, onQueued }) {
  const [file, setFile] = useState(null);
  const [rows, setRows] = useState(null);
  const [check, setCheck] = useState(null);
  const [busy, setBusy] = useState(false);
  const [agree, setAgree] = useState(false);
  const [onlyIssues, setOnlyIssues] = useState(false);
  const input = useRef(null);

  const pick = async (f) => {
    if (!f) return;
    if (!/\.csv$/i.test(f.name)) return toast.error("Upload a .csv file (Excel: File → Save As → CSV)");
    setFile(f); setCheck(null); setAgree(false);
    const grid = parseCsv(await f.text());
    if (grid.length < 2) return toast.error("The file has no payout rows");
    const head = grid[0].map((h) => h.trim());
    const objs = grid.slice(1).map((r) => Object.fromEntries(head.map((h, i) => [h, (r[i] ?? "").trim()])));
    if (objs.length > 1000) return toast.error(`${objs.length} rows — upload at most 1000 at a time`);
    setRows(objs);
    setBusy(true);
    try {
      const { data } = await api.post("/gateway/payouts/bulk/check", { file_name: f.name, rows: objs });
      setCheck(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const send = async () => {
    setBusy(true);
    try {
      const { data } = await api.post("/gateway/payouts/bulk", { file_name: file.name, rows });
      toast.success(`${data.counts.queued || 0} payouts queued — sending one by one`);
      onQueued(data);
    } catch (e) {
      toast.error(errMsg(e));
    } finally {
      setBusy(false);
    }
  };

  const short = check && check.total > check.balance;
  const shown = check ? check.items.filter((it) => !onlyIssues || it.errors.length || it.warnings.length) : [];

  return (
    <Modal wide title="Bulk payout upload" onClose={onClose} footer={<>
      <button className="ui-btn" onClick={onClose} disabled={busy}>Cancel</button>
      <button className="ui-btn ui-btn-primary" data-testid="bulk-send" onClick={send}
        disabled={busy || !check || !check.valid || !agree}>
        {busy && check ? "Queuing…" : check ? `Send ${check.valid} payouts · ${inr(check.total)}` : "Send"}
      </button>
    </>}>
      <div className="bulk-drop" data-testid="bulk-drop" onClick={() => input.current?.click()}
        onDragOver={(e) => e.preventDefault()} onDrop={(e) => { e.preventDefault(); pick(e.dataTransfer.files[0]); }}>
        <FileUp size={22} />
        <div>
          <b>{file ? file.name : "Choose or drop a CSV file"}</b>
          <div className="ui-faint" style={{ fontSize: 12.5 }}>
            Columns: reference_id, beneficiary_name, account_number, ifsc_code, amount (mobile_number, email optional; bank_name & transfer_mode ignored)
          </div>
        </div>
        <input ref={input} type="file" accept=".csv,text/csv" hidden data-testid="bulk-file"
          onChange={(e) => { pick(e.target.files[0]); e.target.value = ""; }} />
      </div>
      <button type="button" className="ui-btn ui-btn-sm" style={{ alignSelf: "flex-start" }} onClick={downloadSample}>
        <Download size={14} /> Sample CSV
      </button>

      {busy && !check && <div className="ui-faint">Checking every row…</div>}
      {check && (
        <>
          <div className="bulk-sum" data-testid="bulk-summary">
            <div><span className="ui-eyebrow">Ready to send</span><b>{check.valid}</b></div>
            <div><span className="ui-eyebrow">With errors (not sent)</span><b style={{ color: check.invalid ? "#b3261e" : undefined }}>{check.invalid}</b></div>
            <div><span className="ui-eyebrow">Total</span><b>{inr(check.total)}</b></div>
            <div><span className="ui-eyebrow">Balance</span><b style={{ color: short ? "#b3261e" : undefined }}>{inr(check.balance)}</b></div>
          </div>
          {short && <Banner tone="danger">Balance is short by {inr(check.total - check.balance)}. Rows are sent in file order; once the balance runs out the rest fail with “insufficient balance” (nothing is debited for them).</Banner>}
          {check.invalid > 0 && <Banner>{check.invalid} row(s) have errors and will be skipped. Fix them and upload those rows again — rows already sent are recognised by reference_id and never paid twice.</Banner>}
          <label className="ui-row" style={{ gap: 6, fontSize: 13 }}>
            <input type="checkbox" checked={onlyIssues} onChange={(e) => setOnlyIssues(e.target.checked)} /> Show only rows with issues
          </label>
          <div className="ui-table-wrap sc-scroll bulk-table">
            <table className="ui-table">
              <thead><tr><th>#</th><th>Reference</th><th>Beneficiary</th><th>Account · IFSC</th><th className="is-num">Amount</th><th>Check</th></tr></thead>
              <tbody>
                {shown.map((it) => (
                  <tr key={it.row} data-testid={`bulk-row-${it.row}`} className={it.errors.length ? "is-bad" : ""}>
                    <td className="ui-mono">{it.row}</td>
                    <td className="ui-mono">{it.reference || "—"}</td>
                    <td>{it.name || "—"}</td>
                    <td className="ui-mono" style={{ fontSize: 12 }}>{it.account || "—"}<br /><span className="ui-faint">{it.ifsc || "—"}</span></td>
                    <td className="is-num">{it.amount ? inr(it.amount) : "—"}</td>
                    <td style={{ fontSize: 12.5 }}>
                      {it.errors.length ? <span style={{ color: "#b3261e" }}>{it.errors.join("; ")}</span> : <span style={{ color: "#0f7a45" }}>OK</span>}
                      {it.warnings.length > 0 && <div style={{ color: "#b26a00" }}>{it.warnings.join("; ")}</div>}
                    </td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
          {check.valid > 0 && (
            <label className="ui-row" style={{ gap: 8, fontSize: 13.5, alignItems: "flex-start" }}>
              <input type="checkbox" data-testid="bulk-agree" checked={agree} onChange={(e) => setAgree(e.target.checked)} style={{ marginTop: 3 }} />
              <span>I checked the beneficiaries. Send <b>{check.valid}</b> bank transfers totalling <b>{inr(check.total)}</b> — they can't be reversed once processed.</span>
            </label>
          )}
        </>
      )}
    </Modal>
  );
}

export function BatchList({ onOpen, bump }) {
  const [data, setData] = useState({ items: [], total: 0, page: 1, pages: 1 });
  const [page, setPage] = useState(1);
  const load = useCallback(() => api.get("/gateway/payouts/bulk", { params: { page, page_size: 10 } })
    .then((r) => setData(r.data)).catch(() => {}), [page]);
  useEffect(() => { load(); }, [load, bump]);
  usePolling(load, 4000, data.items.some((b) => b.status === "running"));
  return (
    <Card pad={false} title="Bulk uploads" sub="Each list is sent one payout at a time, in file order." data-testid="bulk-list">
      {!data.items.length ? <Empty icon="📑" title="No bulk uploads yet">Use “Bulk upload” to send a CSV list of payouts.</Empty> : (
        <div className="ui-table-wrap sc-scroll">
          <table className="ui-table">
            <thead><tr><th>Batch</th><th>Uploaded</th><th>Progress</th><th className="is-num">Sent / total</th><th>Status</th></tr></thead>
            <tbody>
              {data.items.map((b) => {
                const done = (b.counts.sent || 0) + (b.counts.failed || 0);
                const todo = done + (b.counts.queued || 0) + (b.counts.sending || 0);
                return (
                  <tr key={b.id} className="is-click" onClick={() => onOpen(b.id)} data-testid={`bulk-batch-${b.id}`}>
                    <td><span className="ui-cell-main">{b.code}</span><span className="ui-cell-sub">{b.file_name}</span></td>
                    <td className="ui-mono" style={{ fontSize: 12 }}>{when(b.created_at)}</td>
                    <td style={{ minWidth: 140 }}><Progress done={done} total={todo} /><span className="ui-cell-sub">{b.counts.sent || 0} sent · {b.counts.failed || 0} failed · {(b.counts.skipped || 0) + (b.counts.cancelled || 0)} skipped</span></td>
                    <td className="is-num">{inr(b.sent_amount)} <span className="ui-faint">/ {inr(b.total)}</span></td>
                    <td><StatusBadge status={b.status === "running" ? "sending" : "success"} label={b.status === "running" ? "sending" : "finished"} /></td>
                  </tr>
                );
              })}
            </tbody>
          </table>
        </div>
      )}
      {data.total > 10 && <Pager page={data.page} pages={data.pages} total={data.total} size={10} onPage={setPage} sizes={[10]} />}
    </Card>
  );
}

function Progress({ done, total }) {
  const pct = total ? Math.round((done / total) * 100) : 100;
  return <div className="bulk-bar"><span style={{ width: `${pct}%` }} /></div>;
}

export function BatchView({ id, onClose }) {
  const [b, setB] = useState(null);
  const [openId, setOpenId] = useState(null);
  const [filter, setFilter] = useState("all");
  const load = useCallback(() => api.get(`/gateway/payouts/bulk/${id}`).then((r) => setB(r.data)).catch((e) => toast.error(errMsg(e))), [id]);
  useEffect(() => { load(); }, [load]);
  usePolling(load, 2000, !b || b.status === "running" || b.items?.some((it) => ["pending", "creating", "review"].includes(it.payout_status)));

  const cancel = async () => {
    if (!window.confirm("Stop this list? Rows not yet sent are cancelled; rows already sent are not affected.")) return;
    try { setB((await api.post(`/gateway/payouts/bulk/${id}/cancel`)).data); toast.success("Remaining rows cancelled"); }
    catch (e) { toast.error(errMsg(e)); }
  };

  const items = (b?.items || []).filter((it) => filter === "all" || it.status === filter);
  const c = b?.counts || {};
  const done = (c.sent || 0) + (c.failed || 0);
  return (
    <Modal wide title={b ? `${b.code} · ${b.file_name}` : "Bulk upload"} onClose={onClose}
      footer={<>
        {b?.status === "running" && (c.queued || 0) > 0 && <button className="ui-btn" data-testid="bulk-cancel" onClick={cancel}>Stop remaining</button>}
        <button className="ui-btn ui-btn-primary" onClick={onClose}>Close</button>
      </>}>
      {!b ? <div className="ui-faint">Loading…</div> : (
        <>
          <div className="bulk-sum">
            <div><span className="ui-eyebrow">Sent</span><b style={{ color: "#0f7a45" }}>{c.sent || 0}</b></div>
            <div><span className="ui-eyebrow">In queue</span><b>{(c.queued || 0) + (c.sending || 0)}</b></div>
            <div><span className="ui-eyebrow">Failed</span><b style={{ color: c.failed ? "#b3261e" : undefined }}>{c.failed || 0}</b></div>
            <div><span className="ui-eyebrow">Skipped</span><b>{(c.skipped || 0) + (c.cancelled || 0)}</b></div>
            <div><span className="ui-eyebrow">Sent amount</span><b>{inr(b.sent_amount)}</b></div>
          </div>
          <Progress done={done} total={done + (c.queued || 0) + (c.sending || 0)} />
          <div className="ui-row" style={{ gap: 6, flexWrap: "wrap" }}>
            {["all", "queued", "sent", "failed", "skipped", "cancelled"].map((k) => (
              <button key={k} className={`ui-chip ${filter === k ? "is-on" : ""}`} onClick={() => setFilter(k)}>
                {k} {k === "all" ? b.count : c[k] || 0}
              </button>
            ))}
          </div>
          <div className="ui-table-wrap sc-scroll bulk-table" data-testid="bulk-items">
            <table className="ui-table">
              <thead><tr><th>#</th><th>Order ID</th><th>Beneficiary</th><th>Account · IFSC</th><th className="is-num">Amount</th><th>Queue</th><th>Bank status</th></tr></thead>
              <tbody>
                {items.map((it) => (
                  <tr key={it.row} data-testid={`bulk-item-${it.row}`} className={it.payout_id ? "is-click" : ""}
                    onClick={it.payout_id ? () => setOpenId(it.payout_id) : undefined}>
                    <td className="ui-mono">{it.row}</td>
                    <td className="ui-mono" style={{ fontSize: 12 }}>{it.order_id}</td>
                    <td>{it.name}</td>
                    <td className="ui-mono" style={{ fontSize: 12 }}>{it.account}<br /><span className="ui-faint">{it.ifsc}</span></td>
                    <td className="is-num">{inr(it.amount)}</td>
                    <td><StatusBadge status={ITEM_TONE[it.status]} label={it.status} />
                      {it.error && <div style={{ fontSize: 12, color: "#b3261e", marginTop: 3, maxWidth: 260 }}>{it.error}</div>}</td>
                    <td>{it.payout_status ? <StatusBadge status={it.payout_status} /> : "—"}
                      {it.utr && <div className="ui-mono ui-faint" style={{ fontSize: 11.5 }}>UTR {it.utr}</div>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        </>
      )}
      {openId && <TxnDrawer kind="payout" id={openId} onClose={() => setOpenId(null)} onChanged={load} />}
    </Modal>
  );
}
